<template>
  <div
    :class="[
      'style-comments-event',
      { 'style-comments-event--faded': isFaded }
    ]"
  >
    <div
      v-if="item.type === STYLE_EVENT_TYPE.COMMENT"
      :class="[
        'style-comments-event__content',
        'style-comments-event__content--comment',
        item.comment.role === ROLE_TYPE.CREATOR.displayName
          ? 'style-comments-event__content--comment-creator'
          : 'style-comments-event__content--comment-viewer'
      ]"
    >
      <section class="style-comments-event__content-section">
        <div class="style-comments-event__text">
          <div v-html="highlightMentions(item.comment.text)" />
        </div>
        <div
          v-if="item.comment.attachment"
          class="style-comments-event__metadata"
        >
          <div
            class="file-icon"
            :style="fileTypeIcon(item.comment.attachment)"
          />
          <div class="file-info">
            <div
              class="file-name"
              :title="item.comment.attachment.name"
            >
              {{ item.comment.attachment.name }}
            </div>
            <div class="file-size">
              {{ formatFileSize(item.comment.attachment) }}
            </div>
          </div>
          <a
            class="download-icon"
            href="#"
            title="Download"
            @click="downloadAttachment(item)"
          ><i class="el-icon-download" />️</a>
        </div>

        <div class="style-comments-event__metadata-text">
          <strong>
            {{ item.comment.contributor.email }}
            ({{ item.comment.role }})
          </strong>
          {{ formatDateShort(item.updated_at) }}
        </div>
      </section>

      <StitchDropdown
        v-if="showControls"
        ref="optionMenuDropdown"
        :icon-type="ICON_TYPE.MENU"
        :append-to-body="false"
        :dropdown-items="dropdownItems"
      />
    </div>

    <div
      v-else-if="item.type === STYLE_EVENT_TYPE.MAIN_VERSION_SET"
      class="style-comments-event__content style-comments-event__content--info"
    >
      <b>{{ getVersionName(item.style_version_id) }}</b> is now the Main Version
    </div>

    <div
      v-else-if="item.type === STYLE_EVENT_TYPE.NEW_VERSION_ADDED"
      class="style-comments-event__content style-comments-event__content--info"
    >
      <b>New Version</b> posted 😎
    </div>
    <div
      v-else-if="item.type === STYLE_EVENT_TYPE.STATUS_CHANGED"
      class="style-comments-event__content style-comments-event__content--info"
    >
      <b>Status changed from {{ item.asset_status_data.previous_status }} to
        {{ item.asset_status_data.new_status }} by
        {{ item.asset_status_data.changed_by }}</b>
    </div>
    <div
      v-else
      class="style-comments-event__content style-comments-event__content--info"
    >
      Unsupported event type {{ item.type }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  ICON_TYPE,
  STYLE_TYPE,
  DROPDOWN_PLACEMENT
} from '@/constants/dropdownOptions'
import VueTypes from 'vue-types'
import { STYLE_EVENT_TYPE } from '@/constants/styleEventType'
import { StyleEventShape } from '@/types'
import { ROLE_TYPE } from '@/constants/roleType'
import { formatToLocaleDateShort } from '@/services/utils'
import { DataUtils } from '@/mixins/utils.js'

// import filetypes icons
import csv from '@/assets/icons/filetypes/csv.png'
import doc from '@/assets/icons/filetypes/doc.png'
import xls from '@/assets/icons/filetypes/xls.png'
import jpg from '@/assets/icons/filetypes/jpg.png'
import pdf from '@/assets/icons/filetypes/pdf.png'
import png from '@/assets/icons/filetypes/png.png'
import txt from '@/assets/icons/filetypes/txt.png'
import ppt from '@/assets/icons/filetypes/ppt.png'
import zip from '@/assets/icons/filetypes/zip.png'
import defaultFile from '@/assets/icons/filetypes/file.png'

export default {
  name: 'StyleCommentsEvent',

  mixins: [DataUtils],

  props: {
    item: StyleEventShape.loose.isRequired,
    showControls: VueTypes.bool.def(true),
    isFaded: VueTypes.bool.def(false)
  },

  data () {
    return {
      ICON_TYPE,
      STYLE_EVENT_TYPE,
      DROPDOWN_PLACEMENT,
      ROLE_TYPE,
      fileTypeIcons: {
        csv,
        doc,
        defaultFile,
        jpg,
        pdf,
        png,
        txt,
        ppt,
        xls,
        zip
      }
    }
  },

  computed: {
    ...mapGetters(['getVersionById']),

    /**
     * @returns {object[]}
     */
    dropdownItems () {
      const { id: commentId, text } = this.item.comment

      return [
        {
          name: 'Edit Comment',
          method: () => {
            this.$emit('update-comment', { commentId, text })
          }
        },
        {
          name: 'Delete Comment',
          styles: [STYLE_TYPE.DANGER],
          method: () => {
            this.$emit('delete-comment', { commentId })
          }
        }
      ]
    }
  },

  methods: {
    /**
     * @param {object} item
     */
    downloadAttachment (item) {
      this.triggerDownload(item.comment.attachment.file_url)
    },

    /**
     * Returns the background image based on file extension
     *
     * @param   {object} file
     *
     * @returns {string}
     */
    getFileTypeImage (file) {
      if (file) {
        const ext = file.type.toLowerCase()

        if (['jpg', 'jpeg'].includes(ext)) {
          return this.fileTypeIcons.jpg
        } else if (['png', 'gif'].includes(ext)) {
          return this.fileTypeIcons.png
        } else if (['ppt', 'pptx'].includes(ext)) {
          return this.fileTypeIcons.ppt
        } else if (['pdf'].includes(ext)) {
          return this.fileTypeIcons.pdf
        } else if (['doc', 'docx'].includes(ext)) {
          return this.fileTypeIcons.doc
        } else if (['txt'].includes(ext)) {
          return this.fileTypeIcons.txt
        } else if (['xls', 'xlsx'].includes(ext)) {
          return this.fileTypeIcons.xls
        } else if (['csv'].includes(ext)) {
          return this.fileTypeIcons.csv
        } else if (['zip', 'rar', 'tar', 'gz'].includes(ext)) {
          return this.fileTypeIcons.zip
        }
      }

      return this.fileTypeIcons.defaultFile
    },

    /**
     * Returns the style object for the download square
     *
     * @param   {object} item
     *
     * @returns {string}
     */
    fileTypeIcon (item) {
      return {
        backgroundImage: `url(${this.getFileTypeImage(item)})`
      }
    },

    /**
     * @param   {object} file
     *
     * @returns {string}
     */
    formatFileSize (file) {
      if (file) {
        const bytes = file.size_bytes
        const kb = bytes / 1024
        const mb = kb / 1024

        if (mb >= 1) {
          return `${mb.toFixed(0)} Mb`
        } else {
          return `${kb.toFixed(0)} Kb`
        }
      }

      return '0 kb'
    },

    /**
     * @param   {Date}   date
     *
     * @returns {string}
     */
    formatDateShort (date) {
      return formatToLocaleDateShort(date)
    },

    /**
     * @param   {number | null} versionId
     *
     * @returns {string}
     */
    getVersionName (versionId) {
      let version

      if (versionId) {
        version = this.getVersionById(versionId)
      }

      return version ? version.name : 'A deleted version'
    },

    /**
     * @returns {Function}
     */
    findMatchFunction () {
      return match => `<b>${match}</b>`
    },

    /**
     * @param   {string} comment
     *
     * @returns {string}
     */
    highlightMentions (comment) {
      if (comment === '-') {
        return ''
      }

      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g

      return comment.replace(emailRegex, this.findMatchFunction())
    }
  }
}
</script>

<style lang="scss" scoped>
$event-faded-opacity: 0.5;
$square-width: 40px;
$square-height: 55px;
$square-margin-right: 8px;
$square-font-weight: bold;
$font-size-download-icon: 18px;
$font-size-button: 14px;

.style-comments-event {
  width: 100%;
  margin-bottom: spacing(1);
}

.style-comments-event--faded {
  opacity: $event-faded-opacity;
}

.style-comments-event__content {
  margin: auto;
  border-radius: $border-radius-m;
}

.style-comments-event__content--info {
  @include text-label;

  width: spacing(50);
  padding: spacing(1);
  color: $grey;
  font-weight: $font-weight-semibold;
  text-align: center;
  background-color: $grey-ultra-light;
}

.style-comments-event__content--comment {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: spacing(2);
}

.style-comments-event__content--comment-creator {
  background-color: $blue-light;
}

.style-comments-event__content--comment-viewer {
  background-color: $yellow-light;
}

.style-comments-event__content-section {
  margin-right: spacing(1);
}

.style-comments-event__text {
  margin-bottom: spacing(1/2);
  color: $grey-dark;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}

.style-comments-event__metadata-text {
  @include text-label;

  margin: spacing(1) 0;
}

.style-comments-event__metadata {
  position: relative;
  display: flex;
  gap: spacing(1.5);
  align-items: center;
  width: spacing(27);
  height: spacing(9);
  margin: spacing(1/2) 0;
  padding: spacing(2);
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: spacing(2.2);
  border-top-right-radius: 0;
  box-shadow: 0 spacing(1/2) spacing(1.5) rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  /* Icon placeholder */
  .file-icon {
    flex-shrink: 0;
    width: spacing(4);
    height: spacing(4);
    font-size: spacing(4);
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    border-radius: spacing(1);
  }

  /* File info text */
  .file-info {
    flex-grow: 1;
    overflow: hidden;
  }

  .file-name {
    overflow: hidden;
    color: $grey-dark;
    font-weight: $square-font-weight;
    font-size: $font-size-button;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .file-size {
    color: $grey-light;
    font-size: spacing(1.5);
  }

  /* Download icon */
  .download-icon {
    color: $blue-dark;
    font-size: $font-size-download-icon;
    text-decoration: none;
    transition: color 0.2s ease;
  }
}

.style-comments-event__metadata:hover {
  box-shadow: 0 spacing(1) spacing(2) rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}
</style>
